import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  fetchCategories,
  createCategory,
  bulkCreateCategories,
  updateCategory,
  deleteCategory,
} from '../../services/itemService';

const CategoryPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [bulkNames, setBulkNames] = useState('');

  const [page, setPage] = useState(0); // zero-based for DataGrid
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [editDialog, setEditDialog] = useState({
    open: false,
    category: null,
    name: '',
    priority: 999,
    show_in_tabs: true,
    show_as_section: false,
    section_title: '',
    section_order: 999,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const totalPages = Math.ceil(total / rowsPerPage) || 1;

  // Helper to add serial numbers (S/N)
  const addSerialNumbers = (data, pageIndex, perPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: pageIndex * perPage + idx + 1,
    }));

  const loadCategories = async (pageIndex = page, perPage = rowsPerPage, currentSearch = search) => {
    setLoading(true);
    try {
      // Use direct API to keep pagination metadata
      const queryParams = new URLSearchParams();
      queryParams.append('page', pageIndex + 1); // backend is 1-based
      queryParams.append('page_size', perPage);
      if (currentSearch) queryParams.append('search', currentSearch);

      const { default: api } = await import('../../services/api');
      const response = await api.get(`/admin/categories/?${queryParams.toString()}`);
      const data = response.data;

      const results = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      setCategories(results);
      setTotal(Number(data?.count) || results.length);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load categories',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories(page, rowsPerPage, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleCreateSingle = async () => {
    const trimmed = bulkNames.trim();
    if (!trimmed) return;

    // Take first name only for single add
    const firstName = trimmed.split(',')[0].trim();
    if (!firstName) return;

    try {
      await createCategory({ name: firstName });
      setSnackbar({
        open: true,
        message: 'Category created successfully',
        severity: 'success',
      });
      setBulkNames('');
      loadCategories(0, rowsPerPage, search);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to create category',
        severity: 'error',
      });
    }
  };

  const handleBulkCreate = async () => {
    const trimmed = bulkNames.trim();
    if (!trimmed) return;

    try {
      const result = await bulkCreateCategories(trimmed);
      const createdCount = result.created?.length || 0;
      const skippedCount = result.skipped?.length || 0;

      let message = `Created ${createdCount} categories`;
      if (skippedCount > 0) {
        message += `, skipped ${skippedCount} (already exist)`;
      }

      setSnackbar({
        open: true,
        message,
        severity: 'success',
      });
      setBulkNames('');
      loadCategories(page, rowsPerPage, search);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to bulk create categories',
        severity: 'error',
      });
    }
  };

  const handleOpenEdit = (category) => {
    setEditDialog({
      open: true,
      category,
      name: category.name,
      priority: category.priority ?? 999,
      show_in_tabs: category.show_in_tabs ?? true,
      show_as_section: category.show_as_section ?? false,
      section_title: category.section_title ?? '',
      section_order: category.section_order ?? 999,
    });
  };

  const handleSaveEdit = async () => {
    if (!editDialog.category) return;
    const newName = editDialog.name.trim();
    if (!newName) return;

    try {
      await updateCategory(editDialog.category.id, { 
        name: newName,
        priority: Number.isFinite(editDialog.priority) ? editDialog.priority : 999,
        show_in_tabs: !!editDialog.show_in_tabs,
        show_as_section: !!editDialog.show_as_section,
        section_title: editDialog.section_title || '',
        section_order: Number.isFinite(editDialog.section_order) ? editDialog.section_order : 999,
      });
      setSnackbar({
        open: true,
        message: 'Category updated successfully',
        severity: 'success',
      });
      setEditDialog({
        open: false,
        category: null,
        name: '',
        priority: 999,
        show_in_tabs: true,
        show_as_section: false,
        section_title: '',
        section_order: 999,
      });
      loadCategories(page, rowsPerPage, search);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to update category',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete category "${category.name}"? This cannot be undone.`)) {
      return;
    }
    try {
      await deleteCategory(category.id);
      setSnackbar({
        open: true,
        message: 'Category deleted successfully',
        severity: 'success',
      });
      // If deleting might empty the current page, reload with same page index
      loadCategories(page, rowsPerPage, search);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to delete category',
        severity: 'error',
      });
    }
  };

  const columns = [
    {
      field: 'sn',
      headerName: 'S/N',
      width: 80,
      sortable: false,
      filterable: false,
      hideable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 110,
      sortable: true,
      type: 'number',
    },
    {
      field: 'show_in_tabs',
      headerName: 'Show in tabs',
      width: 130,
      sortable: false,
      renderCell: (params) => (params.row.show_in_tabs ? 'Yes' : 'No'),
    },
    {
      field: 'show_as_section',
      headerName: 'Show as section',
      width: 150,
      sortable: false,
      renderCell: (params) => (params.row.show_as_section ? 'Yes' : 'No'),
    },
    {
      field: 'section_title',
      headerName: 'Section title',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'section_order',
      headerName: 'Section order',
      width: 130,
      sortable: true,
      type: 'number',
    },
    {
      field: 'slug',
      headerName: 'Slug',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Item Categories
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadCategories}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Bulk Add / Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <TextField
              label="Search categories"
              value={search}
              onChange={(e) => {
                const val = e.target.value;
                setSearch(val);
                if (searchTimeout) clearTimeout(searchTimeout);
                setSearchTimeout(
                  setTimeout(() => {
                    setPage(0);
                    loadCategories(0, rowsPerPage, val);
                  }, 500),
                );
              }}
              fullWidth
            />

            <TextField
              label="Add categories (comma separated)"
              value={bulkNames}
              onChange={(e) => setBulkNames(e.target.value)}
              placeholder={"Example: Women's Bag, Men's Shoes, Kids Toys"}
              fullWidth
              multiline
              rows={2}
              helperText={
                "You can add a single category or multiple categories separated by commas. "
                + 'Slugs will be inferred like "Women\'s Bag" -> "womens_bag".'
              }
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
                onClick={handleCreateSingle}
                disabled={!bulkNames.trim()}
              >
                Add First Category
              </Button>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleBulkCreate}
                disabled={!bulkNames.trim()}
              >
                Bulk Add Categories
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Categories Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={addSerialNumbers(categories, page, rowsPerPage)}
              columns={columns}
              loading={loading}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={rowsPerPage}
              pagination
              rowCount={total}
              paginationMode="server"
              hideFooter
              disableSelectionOnClick
              getRowId={(row) => row.id}
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeader': {
                  backgroundColor: '#DC143C',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'white',
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#FFF0F3',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-end' },
          mt: 3,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => page > 0 && setPage(page - 1)} disabled={page <= 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
          </Typography>
          <IconButton
            onClick={() => page < totalPages - 1 && setPage(page + 1)}
            disabled={page >= totalPages - 1}
          >
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">Rows per page:</Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'custom') {
                  setCustomRowsActive(true);
                  setCustomRowsInput(String(rowsPerPage));
                } else {
                  setRowsPerPage(Number(value));
                  setPage(0);
                  setCustomRowsActive(false);
                }
              }}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={(e) => setCustomRowsInput(e.target.value.replace(/\D/g, ''))}
              onBlur={() => {
                const val = parseInt(customRowsInput, 10);
                if (val && val > 0) {
                  setRowsPerPage(val);
                  setPage(0);
                }
                setCustomRowsActive(false);
              }}
              autoFocus
              sx={{ width: 100 }}
            />
          )}
        </Box>
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() =>
          setEditDialog({
            open: false,
            category: null,
            name: '',
            priority: 999,
            show_in_tabs: true,
            show_as_section: false,
            section_title: '',
            section_order: 999,
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          Edit Category
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Category name"
              value={editDialog.name}
              onChange={(e) =>
                setEditDialog((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Priority (lower = higher)"
              type="number"
              value={editDialog.priority}
              onChange={(e) =>
                setEditDialog((prev) => ({
                  ...prev,
                  priority: parseInt(e.target.value, 10) || 999,
                }))
              }
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Show in tabs:</Typography>
              <Button
                variant={editDialog.show_in_tabs ? 'contained' : 'outlined'}
                size="small"
                onClick={() =>
                  setEditDialog((prev) => ({
                    ...prev,
                    show_in_tabs: !prev.show_in_tabs,
                  }))
                }
              >
                {editDialog.show_in_tabs ? 'Yes' : 'No'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">Show as section:</Typography>
              <Button
                variant={editDialog.show_as_section ? 'contained' : 'outlined'}
                size="small"
                onClick={() =>
                  setEditDialog((prev) => ({
                    ...prev,
                    show_as_section: !prev.show_as_section,
                  }))
                }
              >
                {editDialog.show_as_section ? 'Yes' : 'No'}
              </Button>
            </Box>
            <TextField
              label="Section title (optional)"
              value={editDialog.section_title}
              onChange={(e) =>
                setEditDialog((prev) => ({
                  ...prev,
                  section_title: e.target.value,
                }))
              }
              fullWidth
              helperText="If empty, the category name will be used as the section title."
            />
            <TextField
              label="Section order (lower = higher)"
              type="number"
              value={editDialog.section_order}
              onChange={(e) =>
                setEditDialog((prev) => ({
                  ...prev,
                  section_order: parseInt(e.target.value, 10) || 999,
                }))
              }
              fullWidth
            />
            <Typography variant="body2" color="text.secondary">
              Categories with lower priority numbers appear first in tab lists. Categories marked
              "Show as section" with a valid section order will appear as horizontal sections on
              the home page, using the section title when provided.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setEditDialog({
                open: false,
                category: null,
                name: '',
                priority: 999,
                show_in_tabs: true,
                show_as_section: false,
                section_title: '',
                section_order: 999,
              })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            onClick={handleSaveEdit}
            disabled={!editDialog.name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryPage;


